import React, { useEffect, useState, useRef } from "react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Edit3, Plus, Upload, X, ImageOff, Link as LinkIcon, Eye } from "lucide-react";

const EMPTY = { title: "", excerpt: "", body: "", cover_image: "", published: true, category: "generale" };
const MAX_UPLOAD_MB = 10;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export default function AdminNews() {
  const [news, setNews] = useState([]);
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const editorRef = useRef(null);

  const load = () => api.get("/admin/news").then((r) => setNews(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (n) => {
    // Le news importate da soloenduro usano `content`; admin usa `body`/`excerpt`.
    // Fallback + auto-genera excerpt dal body se mancante.
    const bodyText = n.body || n.content || "";
    const excerpt = n.excerpt || bodyText.replace(/\s+/g, " ").slice(0, 220).trim();
    setEditing(n.id);
    setForm({
      title: n.title || "",
      excerpt,
      body: bodyText,
      cover_image: n.cover_image || "",
      published: n.published !== false,
      category: n.category || "generale",
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); setUploadProgress(0); };

  const save = async () => {
    // Guard: prevent save while uploading
    if (uploading) {
      toast.error("Attendere il termine dell'upload dell'immagine.");
      return;
    }
    // Client-side validation
    if (!form.title?.trim() || !form.body?.trim()) {
      toast.error("Compila titolo e corpo (obbligatori).");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/news", form);
        toast.success("News creata");
      } else {
        await api.put(`/admin/news/${editing}`, form);
        toast.success("News aggiornata");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa news?")) return;
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success("News eliminata");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore");
    }
  };

  const publicUrl = (n) => {
    const base = window.location.origin;
    const cat = n.category || "racing";
    if (cat === "racing" || cat === "generale") return `${base}/moto/racing-team/news/${n.id}`;
    return `${base}/moto/${cat}/news/${n.id}`;
  };

  const copyLink = async (n) => {
    const url = publicUrl(n);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    } catch {
      const tmp = document.createElement("input");
      tmp.value = url;
      document.body.appendChild(tmp);
      tmp.select();
      try { document.execCommand("copy"); toast.success("Link copiato"); }
      catch { toast.error(`Copia manualmente: ${url}`); }
      finally { document.body.removeChild(tmp); }
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    // Reset input so re-selecting the same file re-triggers upload
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;

    // Type check
    if (!ACCEPTED.includes(file.type) && !file.type.startsWith("image/")) {
      toast.error("Formato non supportato. Usa JPG, PNG, WEBP o GIF.");
      return;
    }
    // Size check
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_UPLOAD_MB) {
      toast.error(`File troppo grande (${sizeMB.toFixed(1)} MB). Massimo consentito: ${MAX_UPLOAD_MB} MB.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 120000, // 2 min for slow connections
      });
      setForm((f) => ({ ...f, cover_image: data.path }));
      toast.success(`Immagine caricata (${(data.size / 1024).toFixed(0)} KB)`);
    } catch (err) {
      const detail = formatApiError(err?.response?.data?.detail);
      if (err.code === "ECONNABORTED") {
        toast.error("Upload troppo lento. Riprova con una connessione più stabile o riduci le dimensioni del file.");
      } else if (err.response?.status === 413) {
        toast.error("File troppo grande per il server.");
      } else {
        toast.error(detail || `Upload fallito: ${err.message}`);
      }
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 800);
    }
  };

  const removeCover = () => setForm((f) => ({ ...f, cover_image: "" }));

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <span className="label-eyebrow text-gray-500">Racing Team</span>
          <h1 className="font-display uppercase text-4xl mt-1">News</h1>
        </div>
        <button onClick={startNew} className="btn-orange label-eyebrow px-5 py-3 inline-flex items-center gap-2" data-testid="news-new">
          <Plus size={14} /> Nuova
        </button>
      </div>

      {editing && (
        <>
          {/* Backdrop overlay to make editor unmissable */}
          <div className="fixed inset-0 bg-black/50 z-40" onClick={cancel} />
          <div
            ref={editorRef}
            className="fixed inset-x-0 top-8 md:top-16 bottom-8 md:bottom-16 md:left-1/2 md:-translate-x-1/2 md:max-w-3xl mx-4 md:mx-auto z-50 bg-white shadow-2xl overflow-y-auto p-6 md:p-8 space-y-4"
            data-testid="news-editor"
          >
          <div className="flex items-center justify-between">
            <h2 className="font-display uppercase text-2xl">{editing === "new" ? "Nuova news" : "Modifica"}</h2>
            <button onClick={cancel} className="text-gray-500"><X size={18} /></button>
          </div>
          <Field label="Titolo*" value={form.title} onChange={(v) => setForm({ ...form, title: v })} tid="news-title" />
          <Field label="Estratto (opzionale)" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} tid="news-excerpt" />
          <div>
            <label className="label-eyebrow text-gray-500 block mb-2">Corpo* <span className="text-gray-400 normal-case tracking-normal">(markdown supportato: **grassetto**, ## titolo, ![alt](url) per immagini)</span></label>
            <textarea
              rows={8}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="w-full border border-gray-300 focus:border-gray-900 outline-none px-3 py-3 font-mono text-sm"
              data-testid="news-body"
            />
          </div>
          <div>
            <label className="label-eyebrow text-gray-500 block mb-2">Cover image <span className="text-gray-400 normal-case tracking-normal">(JPG/PNG/WEBP, max {MAX_UPLOAD_MB} MB)</span></label>
            <div className="flex items-start gap-4 flex-wrap">
              <input type="file" ref={fileRef} accept="image/jpeg,image/png,image/webp,image/gif" onChange={upload} className="hidden" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="border border-gray-300 px-4 py-2 label-eyebrow inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed hover:border-gray-900 transition-colors"
                data-testid="news-upload"
              >
                <Upload size={14} /> {uploading ? `Caricamento… ${uploadProgress}%` : (form.cover_image ? "Sostituisci" : "Carica")}
              </button>
              {form.cover_image && !uploading && (
                <div className="relative group">
                  <img src={mediaUrl(form.cover_image)} alt="cover" className="h-20 w-32 object-cover border border-gray-300" />
                  <button
                    type="button"
                    onClick={removeCover}
                    className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow hover:bg-red-50 hover:text-red-600 transition-colors"
                    title="Rimuovi cover"
                    data-testid="news-remove-cover"
                  >
                    <ImageOff size={14} />
                  </button>
                </div>
              )}
              {uploading && (
                <div className="flex-1 min-w-[200px] max-w-md">
                  <div className="h-2 bg-gray-200 overflow-hidden">
                    <div
                      className="h-full bg-[color:var(--nsa-orange)] transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Caricamento in corso, non chiudere la pagina…</p>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="label-eyebrow text-gray-500 block mb-2">Categoria*</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[color:var(--nsa-orange)]"
              data-testid="news-category"
            >
              <option value="generale">Generale (visibile ovunque)</option>
              <option value="ktm">KTM</option>
              <option value="husqvarna">Husqvarna</option>
              <option value="gasgas">GasGas Trial</option>
              <option value="racing">Racing Team</option>
            </select>
            <p className="mt-1.5 text-xs text-gray-500">
              Determina in quale pagina la news apparirà. &quot;Generale&quot; appare su tutte.
            </p>
          </div>
          <label className="flex items-center gap-2 label-eyebrow text-gray-600">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="news-published" />
            Pubblicata
          </label>
          <div className="flex gap-3">
            <button
              onClick={save}
              disabled={saving || uploading}
              className="btn-orange label-eyebrow px-6 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="news-save"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
            <button onClick={cancel} disabled={saving} className="border border-gray-300 label-eyebrow px-6 py-3 disabled:opacity-60">Annulla</button>
          </div>
        </div>
        </>
      )}

      <div className="mt-10 space-y-4">
        {news.length === 0 && <div className="text-gray-500">Nessuna news ancora. Crea la prima.</div>}
        {news.map((n) => (
          <div key={n.id} className="card-flat p-5 flex items-center gap-4" data-testid={`news-row-${n.id}`}>
            {n.cover_image && (
              <div className="w-24 h-16 bg-cover bg-center flex-shrink-0" style={{ backgroundImage: `url(${mediaUrl(n.cover_image)})` }} />
            )}
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow text-gray-500">
                {new Date(n.created_at).toLocaleDateString("it-IT")} · {n.published ? "Pubblicata" : "Bozza"} · <span className="text-[color:var(--nsa-orange)]">{(n.category || "generale").toUpperCase()}</span>
              </div>
              <div className="font-display uppercase text-xl truncate">{n.title}</div>
              <div className="text-gray-600 text-sm truncate">{n.excerpt || (n.content || n.body || "").slice(0, 160)}</div>
            </div>
            <button onClick={() => copyLink(n)} className="p-2 hover:text-[color:var(--nsa-orange)]" title="Copia link pubblico" data-testid={`news-copy-${n.id}`}><LinkIcon size={16} /></button>
            <a href={publicUrl(n)} target="_blank" rel="noopener noreferrer" className="p-2 hover:text-[color:var(--nsa-orange)]" title="Anteprima pubblica" data-testid={`news-preview-${n.id}`}><Eye size={16} /></a>
            <button onClick={() => startEdit(n)} className="p-2 hover:text-[color:var(--nsa-orange)]" title="Modifica" data-testid={`news-edit-${n.id}`}><Edit3 size={16} /></button>
            <button onClick={() => remove(n.id)} className="p-2 hover:text-red-600" title="Elimina" data-testid={`news-del-${n.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, tid }) {
  return (
    <div>
      <label className="label-eyebrow text-gray-500 block mb-2">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 focus:border-gray-900 outline-none px-3 py-3"
        data-testid={tid}
      />
    </div>
  );
}
